import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  TrendingUp,
  TrendingDown,
  DollarSign, 
  Calculator, 
  PiggyBank, 
  FileText, 
  Clock, 
  FolderKanban, 
  Users,
  Check,
  ChevronDown,
  Shield,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Footer from '@/components/Footer';

// Animated Counter Component
const AnimatedNumber = ({ value, duration = 1500, decimals = 0, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const startTime = Date.now();
          const startValue = 0;
          const endValue = value;

          const animate = () => {
            const now = Date.now();
            const progress = Math.min((now - startTime) / duration, 1);
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            const currentValue = startValue + (endValue - startValue) * easeOutQuart;
            
            setCount(currentValue);

            if (progress < 1) {
              requestAnimationFrame(animate);
            }
          };

          animate();
        }
      },
      { threshold: 0.3 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [value, duration, hasAnimated]);

  const formatNumber = (num) => {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(num);
  };

  return (
    <span ref={elementRef}>
      {formatNumber(count)}{suffix}
    </span>
  );
};

// Feature Journey Gallery Component (Option 3)
const FeatureJourneyGallery = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [timerSeconds, setTimerSeconds] = useState(9252); // 2:34:12 in seconds
  const [sliderPos, setSliderPos] = useState(50); // For comparison slider
  const [isDragging, setIsDragging] = useState(false);
  const comparisonRef = useRef(null);

  // Live timer effect
  useEffect(() => {
    const interval = setInterval(() => {
      setTimerSeconds(prev => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Comparison slider mouse effect
  useEffect(() => {
    const handleMouseUp = () => setIsDragging(false);
    const handleMouseMove = (e) => {
      if (!isDragging || !comparisonRef.current) return;
      const rect = comparisonRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = (x / rect.width) * 100;
      setSliderPos(Math.min(Math.max(percentage, 0), 100));
    };

    if (isDragging) {
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('mousemove', handleMouseMove);
    }
    
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isDragging]);

  const slides = [
    {
      title: "Kunden & Projekte",
      subtitle: "Alles an einem Ort",
      description: "Verwalte deine Kunden und Projekte zentral. Mit Stundensätzen, Budgets und Zahlungszielen.",
      badge: "1️⃣ Organisation",
      badgeColor: "bg-emerald-500",
      mockup: "customers"
    },
    {
      title: "Zeiten erfassen",
      subtitle: "Ein Klick, fertig",
      description: "Starte den Timer oder trage Zeiten manuell ein. Alles wird automatisch deinem Projekt zugeordnet.",
      badge: "2️⃣ Zeiterfassung",
      badgeColor: "bg-emerald-500",
      mockup: "time"
    },
    {
      title: "Rechnungen erstellen",
      subtitle: "Automatisch & rechtssicher",
      description: "Aus deinen Zeiteinträgen werden automatisch Rechnungen. PDF-Export, Swiss QR-Code inklusive.",
      badge: "3️⃣ Rechnungen",
      badgeColor: "bg-emerald-500",
      mockup: "invoice"
    },
    {
      title: "Steuern berechnen",
      subtitle: "Voller Durchblick",
      description: "Sieh in Echtzeit, wie viel dir nach Steuern bleibt. Für DE, AT und CH – automatisch berechnet.",
      badge: "4️⃣ Überblick",
      badgeColor: "bg-emerald-500",
      mockup: "tax"
    },
    {
      title: "Vorher vs. Nachher",
      subtitle: "Die Transformation",
      description: "Von Chaos zu Klarheit – sieh selbst, wie ivoliq deinen Freelancer-Alltag verändert.",
      badge: "✨ Das Ergebnis",
      badgeColor: "bg-gradient-to-r from-emerald-600 to-emerald-500",
      mockup: "comparison"
    }
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setIsAutoPlaying(false);
  };

  const renderMockup = (type) => {
    switch(type) {
      case 'chaos':
        return (
          <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-6 h-full overflow-hidden relative">
            <div className="space-y-3">
              {/* Messy Excel with shake animation */}
              <div className="bg-white rounded shadow-sm p-3 border border-gray-300 transform -rotate-2 animate-shake">
                <div className="text-xs font-mono text-gray-700 space-y-1">
                  <div className="grid grid-cols-4 gap-2 bg-gray-100 p-1.5 rounded">
                    <div>Datum</div><div>Betrag</div><div>MwSt</div><div>???</div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 p-1">
                    <div>15.03</div><div>2500€</div><div className="text-red-600 font-bold animate-pulse">ERROR</div><div>?</div>
                  </div>
                </div>
              </div>
              
              {/* Scattered Post-Its with float animation */}
              <div className="flex gap-2">
                <div className="bg-yellow-100 p-2 rounded text-xs shadow-md transform rotate-3 animate-float">
                  📌 Steuer berechnen!
                </div>
                <div className="bg-pink-100 p-2 rounded text-xs shadow-md transform -rotate-2 animate-float-delayed">
                  ⚠️ Belege fehlen
                </div>
              </div>

              {/* PDF icons with subtle bounce */}
              <div className="flex gap-2">
                <div className="bg-red-100 border border-red-300 rounded p-2 text-xs hover:scale-105 transition-transform">
                  <FileText className="h-4 w-4 text-red-600 mx-auto mb-1" />
                  Rechnung_01.pdf
                </div>
                <div className="bg-red-100 border border-red-300 rounded p-2 text-xs hover:scale-105 transition-transform">
                  <FileText className="h-4 w-4 text-red-600 mx-auto mb-1" />
                  Rechnung_02.pdf
                </div>
              </div>

              <div className="text-center mt-4">
                <div className="inline-block bg-red-500 text-white text-sm px-4 py-2 rounded-full font-semibold animate-pulse">
                  😰 Stressig & unübersichtlich
                </div>
              </div>
            </div>

            {/* Stress particles effect */}
            <div className="absolute top-2 right-2 text-red-300 opacity-50 animate-ping">⚠️</div>
            <div className="absolute bottom-2 left-2 text-yellow-300 opacity-50 animate-ping" style={{animationDelay: '0.5s'}}>📌</div>
          </div>
        );

      case 'customers':
        return (
          <div className="bg-white rounded-lg border border-gray-200 p-4 h-full">
            <div className="space-y-3">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold">Kunden & Projekte</h3>
                <button className="bg-emerald-500 text-white text-xs px-3 py-1 rounded">+ Neu</button>
              </div>
              
              {/* Customer Cards with hover animations */}
              <div className="space-y-2">
                <div className="border border-gray-200 rounded p-2.5 hover:border-emerald-500 hover:shadow-md transition-all cursor-pointer transform hover:-translate-y-0.5">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-semibold text-sm">Tech Startup AG</div>
                      <div className="text-xs text-gray-500">2 aktive Projekte</div>
                    </div>
                    <div className="text-xs text-gray-600 font-semibold">95€/h</div>
                  </div>
                </div>
                
                <div className="border border-gray-200 rounded p-2.5 hover:border-emerald-500 hover:shadow-md transition-all cursor-pointer transform hover:-translate-y-0.5">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-semibold text-sm">Marketing GmbH</div>
                      <div className="text-xs text-gray-500">1 aktives Projekt</div>
                    </div>
                    <div className="text-xs text-gray-600 font-semibold">85€/h</div>
                  </div>
                </div>

                <div className="border-2 border-emerald-300 bg-emerald-50 rounded p-2.5 animate-pulse-subtle cursor-pointer hover:shadow-lg transition-all">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-semibold text-sm flex items-center gap-1.5">
                        Design Studio
                        <span className="text-xs bg-emerald-500 text-white px-1.5 py-0.5 rounded">Aktiv</span>
                      </div>
                      <div className="text-xs text-emerald-600 font-medium">📊 Website Relaunch</div>
                    </div>
                    <div className="text-xs font-semibold text-emerald-700 ml-2">Budget: 8.500€</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'time':
        const hours = Math.floor(timerSeconds / 3600);
        const minutes = Math.floor((timerSeconds % 3600) / 60);
        const seconds = timerSeconds % 60;
        const timeString = `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

        return (
          <div className="bg-white rounded-lg border border-gray-200 p-4 h-full">
            <div className="space-y-3">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold">Zeiterfassung</h3>
                <Clock className="h-4 w-4 text-gray-400 animate-pulse" />
              </div>

              {/* Active Timer with real animation */}
              <div className="bg-emerald-50 border-2 border-emerald-500 rounded-lg p-3 animate-pulse-subtle">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-xs text-emerald-600 font-semibold mb-1 flex items-center gap-1">
                      <span className="inline-block w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                      Läuft gerade
                    </div>
                    <div className="text-sm font-medium">Website Relaunch – Design</div>
                  </div>
                  <div className="text-2xl font-bold text-emerald-700 tabular-nums">{timeString}</div>
                </div>
                <button className="w-full mt-2 bg-emerald-600 text-white text-sm py-1.5 rounded font-semibold hover:bg-emerald-700 transition-colors">
                  ⏸ Stoppen
                </button>
              </div>

              {/* Recent entries */}
              <div className="space-y-1.5">
                <div className="text-xs font-semibold text-gray-600 mb-2">Heutige Einträge</div>
                <div className="flex justify-between items-center text-xs border-b border-gray-100 pb-1.5">
                  <span className="text-gray-600">Meeting mit Kunde</span>
                  <span className="font-semibold">1:30h</span>
                </div>
                <div className="flex justify-between items-center text-xs border-b border-gray-100 pb-1.5">
                  <span className="text-gray-600">Wireframes erstellen</span>
                  <span className="font-semibold">3:15h</span>
                </div>
                <div className="flex justify-between items-center text-xs pt-2">
                  <span className="font-semibold text-gray-700">Heute gesamt</span>
                  <span className="font-bold text-emerald-600">7:19h</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'invoice':
        return (
          <div className="bg-white rounded-lg border border-gray-200 p-4 h-full">
            <div className="space-y-3">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold">Rechnung erstellen</h3>
                <FileText className="h-4 w-4 text-gray-400" />
              </div>

              {/* Invoice Preview */}
              <div className="border-2 border-emerald-500 rounded-lg p-3 bg-gradient-to-br from-white to-emerald-50">
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Rechnung Nr.</span>
                    <span className="font-semibold">2024-042</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">An:</span>
                    <span className="font-semibold">Tech Startup AG</span>
                  </div>
                  <div className="border-t border-gray-200 pt-2 mt-2">
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span>Projektarbeit (24.5h)</span>
                        <span>2.450,00 €</span>
                      </div>
                      <div className="flex justify-between text-gray-500">
                        <span>zzgl. 19% MwSt</span>
                        <span>465,50 €</span>
                      </div>
                    </div>
                  </div>
                  <div className="border-t-2 border-gray-300 pt-2 flex justify-between font-bold">
                    <span className="text-sm">Gesamt</span>
                    <span className="text-lg text-emerald-700">2.915,50 €</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <button className="flex-1 bg-emerald-600 text-white text-sm py-2 rounded font-semibold">
                  📄 PDF erstellen
                </button>
                <button className="flex-1 border border-emerald-600 text-emerald-600 text-sm py-2 rounded font-semibold">
                  ✉️ Versenden
                </button>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-700">
                ✅ Swiss QR-Code automatisch eingefügt
              </div>
            </div>
          </div>
        );

      case 'tax':
        return (
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-200 p-4 h-full flex flex-col justify-center">
            <div className="space-y-3">
              {/* Header with Live indicator */}
              <div className="text-center mb-2">
                <div className="inline-flex items-center gap-2 bg-emerald-50 px-2.5 py-1 rounded-full">
                  <span className="inline-block w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                  <span className="text-[10px] font-semibold text-emerald-700">Live-Berechnung</span>
                </div>
              </div>

              {/* Main Stats - Compact */}
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-white border-2 border-gray-200 rounded-lg p-2 text-center">
                  <div className="text-[9px] text-gray-500 mb-0.5">Einnahmen</div>
                  <div className="text-base font-bold text-gray-900">138.247 €</div>
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-lg p-2 text-center">
                  <div className="text-[9px] text-gray-500 mb-0.5">Steuerlast</div>
                  <div className="text-base font-bold text-red-600">35.311 €</div>
                </div>
              </div>

              {/* Big Result - Compact */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-lg blur-md opacity-40"></div>
                <div className="relative bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-lg p-3 text-white text-center shadow-lg">
                  <div className="text-[9px] opacity-90 mb-0.5 font-medium">💰 Was bleibt dir übrig?</div>
                  <div className="text-2xl font-bold">63.247 €</div>
                  <div className="text-[8px] opacity-75 mt-0.5">Nach allen Steuern & Ausgaben</div>
                </div>
              </div>

              {/* Quick Info - Compact */}
              <div className="grid grid-cols-3 gap-1.5 text-center">
                <div className="bg-white/50 rounded p-1.5">
                  <div className="text-[9px] text-gray-500">Ausgaben</div>
                  <div className="text-xs font-semibold text-gray-700">21.583 €</div>
                </div>
                <div className="bg-white/50 rounded p-1.5">
                  <div className="text-[9px] text-gray-500">Gewinn</div>
                  <div className="text-xs font-semibold text-emerald-600">98.031 €</div>
                </div>
                <div className="bg-white/50 rounded p-1.5">
                  <div className="text-[9px] text-gray-500">USt-Zahllast</div>
                  <div className="text-xs font-semibold text-gray-700">18.527 €</div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'comparison':
        // Before/After Comparison Slider
        return (
          <div className="h-full flex flex-col">
            {/* Labels */}
            <div className="flex justify-between mb-3 text-xs font-semibold">
              <span className="text-red-600">❌ Vorher: Chaos & Stress</span>
              <span className="text-emerald-600">✅ Nachher: Klar mit ivoliq</span>
            </div>

            <div 
              ref={comparisonRef}
              className="relative flex-1 rounded-lg overflow-hidden shadow-xl cursor-col-resize select-none"
              onMouseDown={() => setIsDragging(true)}
            >
              {/* BEFORE: Chaos */}
              <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 p-4">
                <div className="space-y-2 max-w-[80%] mx-auto h-full flex flex-col justify-center pb-12">
                  {/* Messy Excel */}
                  <div className="bg-white rounded shadow-sm p-2 border border-gray-300 transform -rotate-1 animate-shake">
                    <div className="text-[9px] font-mono text-gray-700">
                      <div className="grid grid-cols-3 gap-1 bg-gray-100 p-1 rounded mb-1">
                        <div>Datum</div><div>Betrag</div><div>???</div>
                      </div>
                      <div className="grid grid-cols-3 gap-1 p-1">
                        <div>15.03</div><div>2500€</div><div className="text-red-600 font-bold">ERROR</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Post-Its */}
                  <div className="flex gap-1.5 justify-center">
                    <div className="bg-yellow-100 p-1.5 rounded text-[9px] shadow-sm transform rotate-2">
                      📌 Steuer?
                    </div>
                    <div className="bg-pink-100 p-1.5 rounded text-[9px] shadow-sm transform -rotate-1">
                      ⚠️ Belege
                    </div>
                  </div>
                </div>

                {/* Stress badge - Absolute positioning at bottom of parent container */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2">
                  <div className="bg-red-500 text-white text-[10px] px-3 py-1 rounded-full font-semibold whitespace-nowrap">
                    😰 Stressig
                  </div>
                </div>
              </div>

              {/* AFTER: ivoliq Clean */}
              <div 
                className="absolute inset-0 bg-white"
                style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}
              >
                <div className="h-full p-4">
                  <div className="space-y-2 h-full flex flex-col justify-center pb-12">
                    {/* Clean Dashboard Preview */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="border border-gray-200 rounded p-1.5 text-center">
                        <div className="text-[9px] text-gray-500">Einnahmen</div>
                        <div className="text-sm font-bold">138.247 €</div>
                      </div>
                      <div className="bg-emerald-600 rounded p-1.5 text-center">
                        <div className="text-[9px] text-white">Verfügbar</div>
                        <div className="text-sm font-bold text-white">63.247 €</div>
                      </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="bg-gray-50 border border-gray-200 rounded p-2">
                      <div className="space-y-1 text-[9px]">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Steuerlast</span>
                          <span className="font-semibold">35.311 €</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Gewinn</span>
                          <span className="font-semibold text-emerald-600">98.031 €</span>
                        </div>
                      </div>
                    </div>

                    {/* Result */}
                    <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 rounded p-2 text-white text-center">
                      <div className="text-[9px] opacity-90 mb-0.5">💰 Verfügbar</div>
                      <div className="text-lg font-bold">63.247 €</div>
                    </div>
                  </div>

                  {/* Clean badge - Absolute positioning at bottom of parent container (same level as Stressig) */}
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2">
                    <div className="bg-emerald-500 text-white text-[10px] px-3 py-1 rounded-full font-semibold whitespace-nowrap">
                      😌 Klar & einfach
                    </div>
                  </div>
                </div>
              </div>

              {/* Slider Handle */}
              <div 
                className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg cursor-col-resize z-10"
                style={{ left: `${sliderPos}%` }}
              >
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-xl border-2 border-emerald-500">
                  <div className="flex gap-0.5">
                    <ChevronLeft className="h-3 w-3 text-emerald-600" />
                    <ChevronRight className="h-3 w-3 text-emerald-600" />
                  </div>
                </div>
              </div>

              {/* Instruction - moved to top */}
              <div className="absolute top-2 left-1/2 transform -translate-x-1/2 bg-black/70 backdrop-blur-sm text-white text-[10px] px-3 py-1.5 rounded-full pointer-events-none shadow-lg">
                ← Schieben zum Vergleichen →
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const slide = slides[currentSlide];

  return (
    <div className="relative max-w-5xl mx-auto">
      {/* Progress Bar */}
      <div className="flex gap-1 mb-4">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className="flex-1 h-1 rounded-full overflow-hidden bg-gray-200"
          >
            <div 
              className={`h-full transition-all duration-300 ${
                index === currentSlide ? 'bg-emerald-500' : 'bg-gray-300'
              }`}
              style={{ 
                width: index === currentSlide && isAutoPlaying ? '100%' : index < currentSlide ? '100%' : '0%',
                transition: index === currentSlide && isAutoPlaying ? 'width 4s linear' : 'width 0.3s ease'
              }}
            />
          </button>
        ))}
      </div>

      {/* Main Slide */}
      <div className="relative bg-white rounded-xl shadow-2xl overflow-hidden">
        <div className="grid md:grid-cols-2 gap-8 p-8">
          {/* Left: Text */}
          <div className="flex flex-col justify-center space-y-4">
            <div>
              <div className={`inline-block ${slide.badgeColor} text-white px-3 py-1 rounded-full text-xs font-semibold mb-3`}>
                {slide.badge}
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{slide.title}</h3>
              <p className="text-sm font-semibold text-emerald-600 mb-3">{slide.subtitle}</p>
              <p className="text-gray-600 leading-relaxed">{slide.description}</p>
            </div>

            {/* Step Indicators */}
            <div className="flex gap-2 pt-4">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentSlide 
                      ? 'bg-emerald-500 w-8' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Right: Mockup */}
          <div className="relative">
            <div className="aspect-[3/4] transition-all duration-500 ease-out">
              {renderMockup(slide.mockup)}
            </div>
          </div>
        </div>

        {/* Navigation Arrows - Subtle and on edges */}
        <button
          onClick={prevSlide}
          className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/70 backdrop-blur-sm hover:bg-white/90 p-2 rounded-full shadow-lg transition-all hover:scale-105 z-20 border border-gray-300 opacity-80 hover:opacity-100"
        >
          <ChevronLeft className="h-4 w-4 text-gray-600" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/70 backdrop-blur-sm hover:bg-white/90 p-2 rounded-full shadow-lg transition-all hover:scale-105 z-20 border border-gray-300 opacity-80 hover:opacity-100"
        >
          <ChevronRight className="h-4 w-4 text-gray-600" />
        </button>

      </div>

      {/* Slide Counter + Auto-play indicator */}
      <div className="flex items-center justify-center gap-3 mt-4 text-sm text-gray-500">
        <span>{currentSlide + 1} / {slides.length}</span>
        <button
          onClick={() => setIsAutoPlaying(!isAutoPlaying)}
          className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          title={isAutoPlaying ? 'Pause' : 'Play'}
        >
          {isAutoPlaying ? '⏸' : '▶'}
        </button>
      </div>
    </div>
  );
};

// Before/After Slider Component (Option 2)
const BeforeAfterSlider = () => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    const bounded = Math.min(Math.max(percentage, 0), 100);
    setSliderPosition(bounded);
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      handleMove(e.clientX);
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging && e.touches[0]) {
      handleMove(e.touches[0].clientX);
    }
  };

  useEffect(() => {
    const handleMouseUp = () => setIsDragging(false);
    const handleTouchEnd = () => setIsDragging(false);

    if (isDragging) {
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchend', handleTouchEnd);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('touchmove', handleTouchMove);
    }

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isDragging]);

  return (
    <div className="relative max-w-lg mx-auto">
      {/* Labels */}
      <div className="flex justify-between mb-3 text-sm font-semibold">
        <span className="text-red-600">❌ Vorher: Chaos</span>
        <span className="text-emerald-600">✅ Nachher: ivoliq</span>
      </div>

      <div 
        ref={containerRef}
        className="relative w-full aspect-[4/3] rounded-lg overflow-hidden shadow-2xl cursor-col-resize select-none"
        onMouseDown={() => setIsDragging(true)}
        onTouchStart={() => setIsDragging(true)}
      >
        {/* BEFORE: Chaos Excel */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200">
          <div className="h-full p-4 overflow-hidden">
            {/* Messy Excel-like interface */}
            <div className="bg-white rounded shadow-sm p-2 mb-2 border border-gray-300">
              <div className="flex gap-1 mb-2">
                <div className="text-xs bg-gray-200 px-2 py-1 rounded">Datei</div>
                <div className="text-xs bg-gray-200 px-2 py-1 rounded">Bearbeiten</div>
                <div className="text-xs bg-gray-200 px-2 py-1 rounded">Ansicht</div>
              </div>
              <div className="text-[8px] font-mono text-gray-700 space-y-0.5">
                <div className="grid grid-cols-5 gap-1 bg-gray-100 p-1">
                  <div>Datum</div>
                  <div>Einnahmen</div>
                  <div>Ausgaben</div>
                  <div>MwSt</div>
                  <div>???</div>
                </div>
                <div className="grid grid-cols-5 gap-1 p-1">
                  <div>15.03</div>
                  <div>2500</div>
                  <div>450</div>
                  <div>?</div>
                  <div className="text-red-600">ERROR</div>
                </div>
                <div className="grid grid-cols-5 gap-1 p-1 bg-yellow-50">
                  <div>22.04</div>
                  <div>3200</div>
                  <div>780</div>
                  <div>608</div>
                  <div>?</div>
                </div>
                <div className="grid grid-cols-5 gap-1 p-1">
                  <div>01.05</div>
                  <div>1800</div>
                  <div className="text-red-600">???</div>
                  <div>?</div>
                  <div>?</div>
                </div>
              </div>
            </div>

            {/* Multiple scattered notes */}
            <div className="space-y-1">
              <div className="bg-yellow-100 p-1.5 rounded text-[9px] border-l-2 border-yellow-400">
                📌 Steuer Q1 berechnen!
              </div>
              <div className="bg-pink-100 p-1.5 rounded text-[9px] border-l-2 border-pink-400">
                ⚠️ Belege fehlen...
              </div>
              <div className="bg-blue-100 p-1.5 rounded text-[9px] border-l-2 border-blue-400">
                💭 Wie viel bleibt übrig??
              </div>
            </div>

            {/* Stress indicator */}
            <div className="mt-2 text-center">
              <div className="inline-block bg-red-500 text-white text-[10px] px-3 py-1 rounded-full font-semibold animate-pulse">
                😰 Unübersichtlich & stressig
              </div>
            </div>
          </div>
        </div>

        {/* AFTER: Clean ivoliq Dashboard */}
        <div 
          className="absolute inset-0 bg-white"
          style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
        >
          <div className="h-full p-3">
            <div className="space-y-2">
              {/* Header */}
              <div className="mb-1">
                <h3 className="text-xs font-bold text-gray-900">Steuerberechnung 2024</h3>
                <p className="text-[9px] text-gray-500">Automatische Echtzeit-Kalkulation</p>
              </div>

              {/* Top Row: 4 Overview Cards */}
              <div className="grid grid-cols-4 gap-1">
                <div className="bg-white border border-gray-200 rounded p-1">
                  <div className="text-[9px] text-gray-600 mb-0.5">Einnahmen</div>
                  <div className="text-[10px] font-bold text-gray-900">138.247 €</div>
                </div>
                <div className="bg-white border border-gray-200 rounded p-1">
                  <div className="text-[9px] text-gray-600 mb-0.5">Ausgaben</div>
                  <div className="text-[10px] font-bold text-gray-900">21.583 €</div>
                </div>
                <div className="bg-white border border-gray-200 rounded p-1">
                  <div className="text-[9px] text-gray-600 mb-0.5">Gewinn</div>
                  <div className="text-[10px] font-bold text-gray-900">98.031 €</div>
                </div>
                <div className="bg-emerald-600 rounded p-1">
                  <div className="text-[9px] text-white font-semibold mb-0.5">Verfügbar</div>
                  <div className="text-[10px] font-bold text-white">63.247 €</div>
                </div>
              </div>

              {/* Tax Summary */}
              <div className="bg-gray-50 border border-gray-200 rounded p-1.5">
                <div className="text-[9px] font-semibold text-gray-800 mb-1">Steuern</div>
                <div className="space-y-0.5 text-[9px]">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Einkommensteuer</span>
                    <span className="font-semibold">12.847 €</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Gewerbesteuer</span>
                    <span className="font-semibold">3.231 €</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">USt-Zahllast</span>
                    <span className="font-semibold">18.527 €</span>
                  </div>
                </div>
              </div>

              {/* Final Result */}
              <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 rounded p-2 text-white">
                <div className="text-[9px] opacity-90 mb-0.5">💰 Was bleibt mir übrig?</div>
                <div className="text-lg font-bold">63.247 €</div>
                <div className="text-[8px] opacity-75">Nach allen Steuern & Ausgaben</div>
              </div>
            </div>

            {/* Clean indicator */}
            <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2">
              <div className="bg-emerald-500 text-white text-[10px] px-3 py-1 rounded-full font-semibold">
                😌 Klar & übersichtlich
              </div>
            </div>
          </div>
        </div>

        {/* Slider Handle */}
        <div 
          className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-col-resize"
          style={{ left: `${sliderPosition}%` }}
        >
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-xl border-2 border-emerald-500">
            <div className="flex gap-0.5">
              <ChevronLeft className="h-4 w-4 text-emerald-600" />
              <ChevronRight className="h-4 w-4 text-emerald-600" />
            </div>
          </div>
        </div>

        {/* Instruction hint */}
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black/70 text-white text-xs px-3 py-1 rounded-full pointer-events-none">
          ← Schieben zum Vergleichen →
        </div>
      </div>
    </div>
  );
};

// 3D Tilt Dashboard Component
const TiltDashboard = () => {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const dashboardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!dashboardRef.current) return;
    
    const rect = dashboardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    const tiltX = ((y - centerY) / centerY) * -8;
    const tiltY = ((x - centerX) / centerX) * 8;
    
    setTilt({ x: tiltX, y: tiltY });
  };

  const handleMouseLeave = () => {
    setTilt({ x: 0, y: 0 });
  };

  return (
    <div 
      ref={dashboardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="relative perspective-1000"
      style={{ perspective: '1000px' }}
    >
      {/* Live Badge */}
      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
        <div className="bg-emerald-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 shadow-lg animate-pulse">
          <Zap className="h-3 w-3" />
          Live berechnet in Echtzeit
        </div>
      </div>

      {/* Dashboard with 3D Tilt */}
      <div 
        className="bg-white rounded-lg shadow-2xl border border-gray-200 p-3 overflow-hidden max-w-lg mx-auto transition-transform duration-300 ease-out"
        style={{
          transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          transformStyle: 'preserve-3d'
        }}
      >
        {/* Glow Effect */}
        <div className="absolute inset-0 bg-emerald-500/5 rounded-lg blur-xl -z-10" 
          style={{ 
            opacity: Math.abs(tilt.x) + Math.abs(tilt.y) > 0 ? 0.5 : 0,
            transition: 'opacity 0.3s ease-out'
          }} 
        />

        <div className="space-y-2.5">
          {/* Header */}
          <div className="mb-2">
            <h3 className="text-sm font-bold text-gray-900">Steuerberechnung 2024</h3>
            <p className="text-xs text-gray-500">Automatische Echtzeit-Kalkulation</p>
          </div>

          {/* Top Row: 4 Overview Cards */}
          <div className="grid grid-cols-4 gap-1.5">
            {/* Income */}
            <div className="bg-white border border-gray-200 rounded p-1.5">
              <div className="text-xs text-gray-600 mb-0.5">Einnahmen</div>
              <div className="text-xs font-bold text-gray-900">
                <AnimatedNumber value={138247} suffix=" €" />
              </div>
            </div>

            {/* Expenses */}
            <div className="bg-white border border-gray-200 rounded p-1.5">
              <div className="text-xs text-gray-600 mb-0.5">Ausgaben</div>
              <div className="text-xs font-bold text-gray-900">
                <AnimatedNumber value={21583} suffix=" €" />
              </div>
            </div>

            {/* Profit */}
            <div className="bg-white border border-gray-200 rounded p-1.5">
              <div className="text-xs text-gray-600 mb-0.5">Gewinn</div>
              <div className="text-xs font-bold text-gray-900">
                <AnimatedNumber value={98031} suffix=" €" />
              </div>
            </div>

            {/* Net Available - Highlighted with Pulse */}
            <div className="bg-gray-900 border-2 border-gray-900 rounded p-1.5 relative overflow-hidden">
              <div className="absolute inset-0 bg-emerald-500 opacity-20 animate-pulse" />
              <div className="relative z-10">
                <div className="text-xs text-gray-100 font-semibold mb-0.5">Verfügbar</div>
                <div className="text-xs font-bold text-white">
                  <AnimatedNumber value={63247} suffix=" €" />
                </div>
              </div>
            </div>
          </div>

          {/* VAT Breakdown */}
          <div className="bg-gray-50 border border-gray-200 rounded p-2">
            <div className="text-xs font-semibold text-gray-800 mb-1.5">Umsatzsteuer</div>
            <div className="grid grid-cols-3 gap-1.5 text-xs">
              <div>
                <div className="text-gray-500 text-xs">Eingenommen</div>
                <div className="font-bold text-gray-900">
                  <AnimatedNumber value={21954} suffix=" €" />
                </div>
              </div>
              <div>
                <div className="text-gray-500 text-xs">Gezahlt</div>
                <div className="font-bold text-gray-900">
                  <AnimatedNumber value={3427} suffix=" €" />
                </div>
              </div>
              <div className="bg-white rounded px-1.5 py-1">
                <div className="text-gray-600 font-semibold text-xs">Zahllast</div>
                <div className="font-bold text-gray-900">
                  <AnimatedNumber value={18527} suffix=" €" />
                </div>
              </div>
            </div>
          </div>

          {/* Tax Breakdown - Compact */}
          <div className="bg-white border border-gray-200 rounded p-2">
            <div className="text-xs font-semibold text-gray-800 mb-1.5">Steuern</div>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Einkommensteuer</span>
                <span className="font-semibold text-gray-900">
                  <AnimatedNumber value={12847} suffix=" €" />
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Gewerbesteuer</span>
                <span className="font-semibold text-gray-900">
                  <AnimatedNumber value={3231} suffix=" €" />
                </span>
              </div>
              <div className="flex justify-between items-center pt-1.5 border-t">
                <span className="text-gray-700 font-semibold">Steuerlast gesamt</span>
                <span className="font-bold text-gray-900">
                  <AnimatedNumber value={35311} suffix=" €" />
                </span>
              </div>
            </div>
          </div>

          {/* Final Result - Big Highlight with Enhanced Glow */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-lg blur-md opacity-50" />
            <div className="relative bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-lg p-3 text-white shadow-lg">
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-xs opacity-90 mb-1">💰 Was bleibt mir übrig?</div>
                  <div className="text-2xl font-bold">
                    <AnimatedNumber value={63247} suffix=" €" />
                  </div>
                  <div className="text-xs opacity-75 mt-0.5">Nach allen Steuern & Ausgaben</div>
                </div>
                <TrendingUp className="h-10 w-10 opacity-20" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Testimonial Carousel Component
const TestimonialCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const testimonials = [
    {
      name: "Lisa M.",
      role: "Freelance Designerin, Wien",
      text: "Endlich sehe ich auf einen Blick, was mir nach Steuern wirklich bleibt. Vorher war das immer Bauchgefühl plus Excel.",
      image: "https://images.pexels.com/photos/762020/pexels-photo-762020.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&fit=crop"
    },
    {
      name: "Danielle K.",
      role: "Webentwicklerin, Berlin",
      text: "Ich tracke Zeiten, schreibe Rechnungen und sehe gleichzeitig meine Steuerlast. Drei Tools weniger, ein Kopfproblem weniger.",
      image: "https://images.pexels.com/photos/2381069/pexels-photo-2381069.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&fit=crop"
    },
    {
      name: "Mira S.",
      role: "Marketing Freelancerin, Zürich",
      text: "Vor ivoliq wusste ich nie, wie viel ich für Steuern zurücklegen muss. Jetzt plane ich meine Ausgaben viel entspannter.",
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop&crop=faces"
    },
    {
      name: "Thomas B.",
      role: "IT-Berater, München",
      text: "Die Zeiterfassung mit automatischer Rechnungserstellung spart mir jede Woche mehrere Stunden. Endlich kann ich mich auf meine Kunden konzentrieren.",
      image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop&crop=faces"
    },
    {
      name: "Sarah K.",
      role: "Content Creatorin, Hamburg",
      text: "Als Österreicherin war ich immer unsicher bei der Steuerberechnung. ivoliq rechnet alles automatisch für AT aus – perfekt!",
      image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=100&h=100&fit=crop&crop=faces"
    },
    {
      name: "Marco H.",
      role: "Fotograf, Basel",
      text: "Die Schweizer QR-Rechnungen funktionieren einwandfrei. Endlich ein Tool, das die Schweiz nicht vergisst!",
      image: "https://images.unsplash.com/photo-1629425733761-caae3b5f2e50?w=100&h=100&fit=crop&crop=faces"
    }
  ];

  // Auto-rotation effect - rotates every 5 seconds
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % 6); // 6 testimonials
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div 
      className="relative py-16" 
      onMouseEnter={() => setIsPaused(true)} 
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Navigation Buttons */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white hover:bg-gray-50 shadow-xl rounded-full p-3 transition-all hover:scale-110 border border-gray-200"
        aria-label="Previous testimonial"
      >
        <ChevronLeft className="w-6 h-6 text-gray-700" />
      </button>
      
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white hover:bg-gray-50 shadow-xl rounded-full p-3 transition-all hover:scale-110 border border-gray-200"
        aria-label="Next testimonial"
      >
        <ChevronRight className="w-6 h-6 text-gray-700" />
      </button>

      {/* Cards Container - Fixed 3 visible cards */}
      <div className="relative overflow-visible px-16">
        <div className="flex justify-center items-center gap-6">
          {/* Show 3 cards: prev, current, next */}
          {[-1, 0, 1].map((offset) => {
            const index = (currentIndex + offset + testimonials.length) % testimonials.length;
            const testimonial = testimonials[index];
            const isCenter = offset === 0;
            
            return (
              <div
                key={`${index}-${offset}`}
                className={`transition-all duration-700 ease-in-out ${
                  isCenter 
                    ? 'scale-110 z-10' 
                    : 'scale-90 opacity-50'
                }`}
                style={{ 
                  flex: '0 0 340px',
                  transformOrigin: 'center center'
                }}
              >
                <Card className={`w-full ${
                  isCenter 
                    ? 'shadow-2xl border-4 border-emerald-500 bg-white ring-4 ring-emerald-100' 
                    : 'shadow-md bg-white/70 border-2 border-gray-200'
                }`}>
                  <CardContent className="p-6 flex flex-col" style={{ minHeight: '280px' }}>
                    <div className="flex items-start gap-4 mb-4">
                      <img 
                        src={testimonial.image} 
                        alt={testimonial.name}
                        className={`rounded-full object-cover flex-shrink-0 ${
                          isCenter 
                            ? 'w-16 h-16 ring-4 ring-emerald-300' 
                            : 'w-14 h-14 ring-2 ring-gray-200'
                        }`}
                        onError={(e) => {
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(testimonial.name)}&background=10b981&color=fff&size=64`;
                        }}
                      />
                      <div>
                        <p className={`font-semibold text-gray-900 ${isCenter ? 'text-base' : 'text-sm'}`}>
                          {testimonial.name}
                        </p>
                        <p className={`text-gray-500 ${isCenter ? 'text-xs' : 'text-[10px]'}`}>
                          {testimonial.role}
                        </p>
                      </div>
                    </div>
                    <p className={`text-gray-700 leading-relaxed flex-grow ${
                      isCenter ? 'text-sm' : 'text-xs'
                    }`}>
                      „{testimonial.text}"
                    </p>
                    <div className="mt-4 flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className={`text-yellow-400 ${isCenter ? 'text-lg' : 'text-sm'}`}>★</span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      </div>

      {/* Dots Indicator with Animation */}
      <div className="flex justify-center gap-2 mt-8">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`transition-all duration-500 ${
              index === currentIndex 
                ? 'w-8 h-3 bg-emerald-600' 
                : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
            } rounded-full relative overflow-hidden`}
            aria-label={`Go to testimonial ${index + 1}`}
          >
            {index === currentIndex && !isPaused && (
              <div 
                className="absolute top-0 left-0 h-full bg-emerald-400"
                style={{
                  animation: 'progress 5s linear',
                  width: '100%'
                }}
              />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

// Add custom styles
const style = document.createElement('style');
style.textContent = `
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
  
  @keyframes progress {
    from {
      width: 0%;
    }
    to {
      width: 100%;
    }
  }
  
  @keyframes shake {
    0%, 100% { transform: rotate(-2deg) translateX(0); }
    25% { transform: rotate(-2.5deg) translateX(-1px); }
    75% { transform: rotate(-1.5deg) translateX(1px); }
  }
  
  @keyframes float {
    0%, 100% { transform: rotate(3deg) translateY(0px); }
    50% { transform: rotate(3deg) translateY(-5px); }
  }
  
  @keyframes float-delayed {
    0%, 100% { transform: rotate(-2deg) translateY(0px); }
    50% { transform: rotate(-2deg) translateY(-5px); }
  }
  
  @keyframes glow {
    0%, 100% { box-shadow: 0 0 10px rgba(16, 185, 129, 0.3); }
    50% { box-shadow: 0 0 20px rgba(16, 185, 129, 0.5); }
  }
  
  @keyframes pulse-subtle {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.95; }
  }
  
  .animate-shake {
    animation: shake 3s ease-in-out infinite;
  }
  
  .animate-float {
    animation: float 2s ease-in-out infinite;
  }
  
  .animate-float-delayed {
    animation: float-delayed 2s ease-in-out infinite;
    animation-delay: 0.5s;
  }
  
  .animate-glow {
    animation: glow 2s ease-in-out infinite;
  }
  
  .animate-pulse-subtle {
    animation: pulse-subtle 3s ease-in-out infinite;
  }
`;
document.head.appendChild(style);

export default function NewLanding() {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white to-gray-50 pt-20 pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text & CTAs */}
            <div className="space-y-8">
              <div className="space-y-6">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                  Alles für deine Finanzen & Steuern als Freelancer – automatisch, verständlich, stressfrei.
                </h1>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                  Einnahmen, Ausgaben, Steuerlast, SVS & Rechnungen – alles in einem klaren Dashboard. Starte in weniger als 2 Minuten.
                </p>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg">
                    Jetzt kostenlos starten
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="lg" variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 px-8 py-6 text-lg">
                    Zum Login
                  </Button>
                </Link>
              </div>

              {/* Trust Element */}
              <div className="grid grid-cols-2 gap-3 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-emerald-600" />
                  <span>Kein Risiko</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-emerald-600" />
                  <span>Keine Kreditkarte</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-emerald-600" />
                  <span>DSGVO-konform</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-emerald-600" />
                  <span>Server in der EU</span>
                </div>
              </div>
            </div>

            {/* Right Column - Feature Journey Gallery */}
            <div className="relative">
              <FeatureJourneyGallery />
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="bg-gray-50 py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-base sm:text-lg text-gray-700 font-medium">
            Bereits von Freelancern in AT, DE & CH getestet und empfohlen.
          </p>
          <div className="flex justify-center items-center gap-6 mt-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-blue-600" />
              <span className="font-medium">DSGVO-konform</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-blue-600" />
              <span className="font-medium">Hosting in der EU</span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem & Solution Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Steuern, Finanzen, Chaos? Kennst du das?
            </h2>
            
            <div className="space-y-6 text-left">
              <p className="text-lg text-gray-700 leading-relaxed">
                Du verdienst gut, aber am Jahresende kommt die böse Überraschung: Steuernachzahlungen, die du nicht eingeplant hast. Rechnungen in Excel, Zeit in Apps, Steuern beim Steuerberater – alles verstreut. Du weißt nie genau, was dir wirklich bleibt.
              </p>
              
              <div className="bg-emerald-50 border-l-4 border-emerald-600 p-6 rounded-r-lg">
                <p className="text-lg text-gray-900 leading-relaxed">
                  <strong className="text-emerald-900">ivoliq bündelt alles in einem klaren Cockpit:</strong> Deine Einnahmen, Ausgaben, Steuerlast und Rücklagen – live und verständlich. Kein Steuer-Chaos mehr. Keine Überraschungen. Nur Klarheit.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Kernfeatures Section */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Deine Finanzen & Steuern – endlich verständlich
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Feature 1 */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 space-y-4">
                <div className="h-12 w-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Einnahmen & Ausgaben – automatisch kategorisiert
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Kein Excel-Chaos mehr.
                </p>
              </CardContent>
            </Card>

            {/* Feature 2 */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 space-y-4">
                <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Calculator className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Steuerlast in Echtzeit – ohne Steuerwissen
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Endlich verständlich erklärt.
                </p>
              </CardContent>
            </Card>

            {/* Feature 3 */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 space-y-4">
                <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <PiggyBank className="h-6 w-6 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Rechnungen in 30 Sekunden erstellt
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Nummernkreis & Branding automatisch.
                </p>
              </CardContent>
            </Card>

            {/* Feature 4 */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 space-y-4">
                <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FileText className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Sozialabgaben & Einkommensteuer transparent
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Ohne komplizierte Formulare.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Single Testimonial After Kernfeatures */}
      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="shadow-2xl border-4 border-emerald-500 bg-white ring-4 ring-emerald-100">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <img 
                  src="https://images.pexels.com/photos/762020/pexels-photo-762020.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&fit=crop" 
                  alt="Lisa M."
                  className="w-16 h-16 rounded-full object-cover flex-shrink-0 ring-4 ring-emerald-300"
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=Lisa+M&background=10b981&color=fff&size=64`;
                  }}
                />
                <div>
                  <p className="font-semibold text-gray-900 text-lg">
                    Lisa M.
                  </p>
                  <p className="text-gray-500 text-sm">
                    Freelance Designerin, Wien
                  </p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed text-base mb-4">
                „Endlich sehe ich auf einen Blick, was mir nach Steuern wirklich bleibt. Vorher war das immer Bauchgefühl plus Excel."
              </p>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-yellow-400 text-xl">★</span>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA before Bonus Features */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
            Starte jetzt kostenlos
          </h2>
          <p className="text-base sm:text-lg text-gray-600 mb-6">
            Keine Kreditkarte erforderlich. In weniger als 2 Minuten eingerichtet.
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-10 py-5 text-lg font-bold">
              Jetzt kostenlos starten
            </Button>
          </Link>
        </div>
      </section>

      {/* Bonus Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Mehr als nur Steuern
            </h2>
          </div>

          <div className="grid sm:grid-cols-3 gap-8">
            {/* Bonus 1 */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 space-y-4">
                <div className="h-12 w-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Clock className="h-6 w-6 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Zeiterfassung
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Erfasse deine Arbeitszeit pro Projekt und sieh, wie profitabel deine Aufträge wirklich sind.
                </p>
              </CardContent>
            </Card>

            {/* Bonus 2 */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 space-y-4">
                <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FolderKanban className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Projektverwaltung
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Behalte alle Projekte im Blick: Status, Budget, Deadlines – alles an einem Ort.
                </p>
              </CardContent>
            </Card>

            {/* Bonus 3 */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 space-y-4">
                <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Users className="h-6 w-6 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Kundendatenbank & Rechnungen
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Erstelle rechtskonforme Rechnungen und verwalte alle Kundendaten zentral.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              So arbeitest du mit ivoliq – von Auftrag bis Steuer
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-2">
              Nach deiner Anmeldung geht es sofort los – ohne Setup, ohne Komplexität.
            </p>
            <p className="text-base text-gray-500 max-w-3xl mx-auto">
              Der komplette Workflow in einem Tool: von Kunden & Projekten über Zeiten und Rechnungen
              bis hin zur Steuerberechnung.
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-8">
            {/* Step 1 */}
            <div className="text-center space-y-4">
              <div className="mx-auto h-12 w-12 bg-emerald-600 text-white rounded-full flex items-center justify-center text-lg font-bold">
                1
              </div>
              <h3 className="text-lg font-bold text-gray-900">Kunden & Projekte anlegen</h3>
              <p className="text-sm text-gray-600">
                Lege deine Kunden und Projekte an, inkl. Stundensätze, Budgets und Zahlungszielen.
              </p>
              <p className="text-xs text-emerald-600 font-semibold">
                → In 30 Sekunden erstellt
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center space-y-4">
              <div className="mx-auto h-12 w-12 bg-emerald-600 text-white rounded-full flex items-center justify-center text-lg font-bold">
                2
              </div>
              <h3 className="text-lg font-bold text-gray-900">Zeiten erfassen</h3>
              <p className="text-sm text-gray-600">
                Zeichne deine Arbeitszeit je Projekt mit dem integrierten Timer auf und verbuche sie direkt.
              </p>
              <p className="text-xs text-emerald-600 font-semibold">
                → Timer starten oder manuell eintragen
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center space-y-4">
              <div className="mx-auto h-12 w-12 bg-emerald-600 text-white rounded-full flex items-center justify-center text-lg font-bold">
                3
              </div>
              <h3 className="text-lg font-bold text-gray-900">Rechnungen erstellen</h3>
              <p className="text-sm text-gray-600">
                Erzeuge aus deinen Projektzeiten oder Pauschalen rechtskonforme Rechnungen als PDF.
              </p>
              <p className="text-xs text-emerald-600 font-semibold">
                → Automatisch aus Zeiteinträgen generiert
              </p>
            </div>

            {/* Step 4 */}
            <div className="text-center space-y-4">
              <div className="mx-auto h-12 w-12 bg-emerald-600 text-white rounded-full flex items-center justify-center text-lg font-bold">
                4
              </div>
              <h3 className="text-lg font-bold text-gray-900">Ausgaben erfassen</h3>
              <p className="text-sm text-gray-600">
                Dokumentiere deine laufenden Ausgaben und Belege, damit sie automatisch in die Auswertung einfließen.
              </p>
              <p className="text-xs text-emerald-600 font-semibold">
                → Belege hochladen oder manuell eingeben
              </p>
            </div>

            {/* Step 5 */}
            <div className="text-center space-y-4">
              <div className="mx-auto h-12 w-12 bg-emerald-600 text-white rounded-full flex items-center justify-center text-lg font-bold">
                5
              </div>
              <h3 className="text-lg font-bold text-gray-900">Steuern berechnen lassen</h3>
              <p className="text-sm text-gray-600">
                ivoliq berechnet deine Steuerlast, sinnvolle Rücklagen und zeigt dir, was dir netto bleibt.
              </p>
              <p className="text-xs text-emerald-600 font-semibold">
                → Steuerlast & Rücklagen in Echtzeit
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Gemacht für Freelancer, die Klarheit wollen
            </h2>
            <p className="text-lg text-gray-600">
              ivoliq ist für dich, wenn du arbeitest als:
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {[
              'Designer (UX, UI, Grafik, Motion)',
              'Developer (Web, Mobile, Backend, Frontend)',
              'Marketer (SEO, Ads, Content, Social Media)',
              'Berater (Business, Prozess, IT, Strategie)',
              'Creator (Content, Video, Fotografie, Copywriting)',
              'Freelancer in allen anderen Bereichen'
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3">
                <Check className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>

          <p className="text-center text-gray-600 mt-8">
            Egal ob du 20.000 € oder 200.000 € im Jahr verdienst – ivoliq gibt dir die Klarheit, die du brauchst.
          </p>
        </div>
      </section>

      {/* CTA Section before Why ivoliq */}
      <section className="py-20 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Bereit für klare Finanzen?
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 mb-8">
            Starte kostenlos – in weniger als 2 Minuten.
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-12 py-6 text-xl font-bold">
              Jetzt kostenlos starten
            </Button>
          </Link>
          <div className="flex justify-center gap-6 mt-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5 text-emerald-600" />
              <span>Kein Risiko</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5 text-emerald-600" />
              <span>Keine Kreditkarte nötig</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why ivoliq Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">
              Warum ivoliq?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4 p-6 bg-emerald-50 rounded-lg border-l-4 border-emerald-600">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold">
                  ✓
                </div>
              </div>
              <div>
                <p className="text-gray-800 leading-relaxed">
                  <strong>Spart dir pro Monat mehrere Stunden Buchhaltung</strong>
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-emerald-50 rounded-lg border-l-4 border-emerald-600">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold">
                  ✓
                </div>
              </div>
              <div>
                <p className="text-gray-800 leading-relaxed">
                  <strong>Verhindert Steuerüberraschungen durch Echtzeit-Berechnung</strong>
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-emerald-50 rounded-lg border-l-4 border-emerald-600">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold">
                  ✓
                </div>
              </div>
              <div>
                <p className="text-gray-800 leading-relaxed">
                  <strong>Ersetzt Excel, Papier-Chaos & komplizierte Tools</strong>
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-emerald-50 rounded-lg border-l-4 border-emerald-600">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold">
                  ✓
                </div>
              </div>
              <div>
                <p className="text-gray-800 leading-relaxed">
                  <strong>Entwickelt für Freelancer in DE, AT & CH – mit Fokus auf Verständlichkeit</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section with Auto-Rotation */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Was andere Freelancer über ivoliq sagen
            </h2>
            <p className="text-lg text-gray-600 mb-2">
              Erste Nutzer:innen testen ivoliq bereits in ihrem echten Freelancer-Alltag.
            </p>
          </div>

          <TestimonialCarousel />
        </div>
      </section>

      {/* Pricing Section */}
      <section className="bg-gradient-to-b from-emerald-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Transparente Preise – für jede Phase deines Business
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Starte kostenlos oder sichere dir jetzt den dauerhaften Early-Supporter-Zugang
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {/* Free Plan */}
            <div className="relative bg-white rounded-2xl shadow-xl p-8 flex flex-col">
              {/* Plan Header */}
              <div className="text-center mb-6">
                <h3 className="text-3xl font-bold text-gray-900 mb-2">Free</h3>
                <p className="text-gray-600 mb-4">Zum Ausprobieren</p>
                <div className="mb-4">
                  <span className="text-5xl font-bold text-gray-900">€0</span>
                  <span className="text-gray-600 ml-2 block text-sm mt-1">Kostenlos für immer</span>
                </div>
              </div>

              {/* Capacity */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-bold text-gray-800 mb-2">📊 Kapazität:</p>
                <div className="text-sm text-gray-700">
                  <span className="font-semibold">1</span> Kunde • <span className="font-semibold">1</span> Projekt
                </div>
              </div>

              {/* Included Features */}
              <div className="flex-1 mb-6">
                <p className="text-sm font-bold text-gray-800 mb-3">✓ Enthalten:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Zeiterfassung mit Timer</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Ausgaben dokumentieren</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">PDF-Rechnungen erstellen</span>
                  </li>
                </ul>
              </div>

              {/* CTA Button */}
              <Link to="/signup">
                <Button className="w-full py-3 px-4 rounded-lg font-bold text-sm bg-emerald-600 hover:bg-emerald-700 text-white">
                  Jetzt kostenlos starten
                </Button>
              </Link>
            </div>

            {/* Basic Plan */}
            <div className="relative bg-white rounded-2xl shadow-xl p-8 flex flex-col">
              {/* Plan Header */}
              <div className="text-center mb-6">
                <h3 className="text-3xl font-bold text-gray-900 mb-2">Basic</h3>
                <p className="text-gray-600 mb-4">Für Starter</p>
                <div className="mb-4">
                  <span className="text-5xl font-bold text-gray-900">€9</span>
                  <span className="text-gray-600 ml-2">/ Monat</span>
                  <p className="text-xs text-gray-500 mt-2">zzgl. MwSt.</p>
                </div>
              </div>

              {/* Capacity */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-bold text-gray-800 mb-2">📊 Kapazität:</p>
                <div className="text-sm text-gray-700">
                  <span className="font-semibold">2</span> Kunden • <span className="font-semibold">4</span> Projekte
                </div>
              </div>

              {/* Included Features */}
              <div className="flex-1 mb-6">
                <p className="text-sm font-bold text-gray-800 mb-3">✓ Enthalten:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Zeiterfassung mit Timer</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Ausgaben dokumentieren</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">PDF-Rechnungen erstellen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Automatische Rechnungen aus Zeiteinträgen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Dashboard mit Umsatzübersicht</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Finanz-Auswertungen</span>
                  </li>
                </ul>
              </div>

              {/* CTA Button */}
              <Link to="/signup">
                <Button className="w-full py-3 px-4 rounded-lg font-bold text-sm bg-emerald-600 hover:bg-emerald-700 text-white">
                  Jetzt kostenlos starten
                </Button>
              </Link>
            </div>

            {/* Pro Plan */}
            <div className="relative bg-white rounded-2xl shadow-xl p-8 flex flex-col">
              {/* Plan Header */}
              <div className="text-center mb-6">
                <h3 className="text-3xl font-bold text-gray-900 mb-2">Pro</h3>
                <p className="text-gray-600 mb-4">Für Profis</p>
                <div className="mb-4">
                  <span className="text-5xl font-bold text-gray-900">€19</span>
                  <span className="text-gray-600 ml-2">/ Monat</span>
                  <p className="text-xs text-gray-500 mt-2">zzgl. MwSt.</p>
                </div>
              </div>

              {/* Capacity */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-bold text-gray-800 mb-2">📊 Kapazität:</p>
                <div className="text-sm text-gray-700">
                  <span className="font-semibold">∞</span> Kunden • <span className="font-semibold">∞</span> Projekte
                </div>
              </div>

              {/* Included Features */}
              <div className="flex-1 mb-6">
                <p className="text-sm font-bold text-gray-800 mb-3">✓ Enthalten:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Zeiterfassung mit Timer</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Ausgaben dokumentieren</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">PDF-Rechnungen erstellen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Automatische Rechnungen aus Zeiteinträgen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Dashboard mit Umsatzübersicht</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Finanz-Auswertungen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Steuerberechnung (DE/AT/CH)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">Einkommensteuer, USt, Sozialabgaben</span>
                  </li>
                </ul>
              </div>

              {/* CTA Button */}
              <Link to="/signup">
                <Button className="w-full py-3 px-4 rounded-lg font-bold text-sm bg-emerald-600 hover:bg-emerald-700 text-white">
                  Jetzt kostenlos starten
                </Button>
              </Link>
            </div>

            {/* Lifetime Bundle */}
            <div className="relative bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl shadow-2xl p-8 border-2 border-amber-400 flex flex-col">
              <div className="absolute -top-3 left-0 right-0 flex justify-center z-10">
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-3 py-1 rounded-full font-bold text-xs flex items-center gap-1">
                  ⭐ Early Supporter
                </div>
              </div>

              {/* Plan Header */}
              <div className="text-center mb-4 mt-3">
                <h3 className="text-3xl font-bold text-gray-900 mb-2">Lifetime</h3>
                <p className="text-gray-600 mb-3 text-sm">Einmalig zahlen – für immer nutzen</p>
                <div className="mb-3">
                  <span className="text-5xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                    €199
                  </span>
                  <span className="text-gray-600 block text-xs mt-1">Einmalig – kein Abo</span>
                  <p className="text-xs text-gray-500 mt-1">zzgl. MwSt.</p>
                </div>
                <div className="inline-block bg-amber-100 border border-amber-300 rounded-full px-3 py-1">
                  <span className="text-xs font-semibold text-amber-900 flex items-center gap-1">
                    <span className="animate-pulse">🔥</span>
                    Begrenzt auf 50 Stück
                  </span>
                </div>
              </div>

              {/* Capacity */}
              <div className="mb-4 p-3 bg-white/70 rounded-lg">
                <p className="text-sm font-bold text-gray-800 mb-2">📊 Kapazität:</p>
                <div className="text-sm text-gray-700">
                  <span className="font-semibold">∞</span> Kunden • <span className="font-semibold">∞</span> Projekte
                </div>
              </div>

              {/* Features */}
              <div className="flex-1 mb-4">
                <p className="text-sm font-bold text-gray-800 mb-2">✓ Enthalten:</p>
                <ul className="space-y-1.5">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">Alle Pro Features</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">Automatische Rechnungen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">Dashboard & Analytics</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700">Steuerberechnung (DE/AT/CH)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700 font-semibold">Lebenslanger Zugang</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700 font-semibold">Alle zukünftigen Updates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700 font-semibold">Nie wieder zahlen</span>
                  </li>
                </ul>
              </div>

              {/* CTA Button */}
              <Link to="/login?intent=lifetime">
                <Button className="w-full py-3 px-4 rounded-lg font-bold text-sm bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white transition-all transform hover:scale-105">
                  🎉 Lifetime für 199 € sichern
                </Button>
              </Link>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-sm text-gray-600">
              Alle Preise zzgl. MwSt. · <Link to="/pricing" className="text-emerald-600 hover:underline font-medium">Komplette Preisübersicht ansehen</Link>
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Häufige Fragen
            </h2>
          </div>

          <div className="space-y-4">
            {[
              {
                question: 'Für welche Länder funktioniert ivoliq?',
                answer: 'ivoliq berechnet Steuern für Freelancer in Österreich, Deutschland und der Schweiz. Wir berücksichtigen länderspezifische Steuerregeln.'
              },
              {
                question: 'Brauche ich noch einen Steuerberater?',
                answer: 'ivoliq ersetzt keinen Steuerberater, gibt dir aber die Klarheit, die du für Entscheidungen brauchst. Viele Nutzer nutzen ivoliq für die Übersicht und den Steuerberater nur noch für die finale Steuererklärung.'
              },
              {
                question: 'Wie sicher sind meine Daten?',
                answer: 'Alle Daten werden verschlüsselt auf EU-Servern (Hetzner, Deutschland) gespeichert. ivoliq ist zu 100 % DSGVO-konform.'
              },
              {
                question: 'Was ist nach dem Early-Supporter-Angebot?',
                answer: 'Nach der Early-Access-Phase wird ivoliq ein Abo-Modell (voraussichtlich 19–29 € monatlich). Als Early-Supporter zahlst du nur einmal und hast dauerhaften Zugang.'
              },
              {
                question: 'Kann ich ivoliq auch für meine GmbH nutzen?',
                answer: 'Aktuell ist ivoliq für Einzelunternehmer und Freelancer optimiert. Eine GmbH-Version ist in Planung.'
              },
              {
                question: 'Gibt es eine Geld-zurück-Garantie?',
                answer: 'Ja. 30 Tage Geld-zurück-Garantie, ohne Wenn und Aber.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-lg border border-gray-200">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </h3>
                  <ChevronDown 
                    className={`h-5 w-5 text-gray-500 flex-shrink-0 transition-transform ${
                      openFaq === index ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
